import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { toast } from 'sonner';
import {
  User,
  Shield,
  Palette,
  Bot,
  Server,
  Clock,
  Save,
  Eye,
  EyeOff,
  RefreshCw,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  AlertCircle,
  Settings as SettingsIcon,
  Lock,
  Globe,
  Upload,
  Database,
  FileText,
  Mail,
  Phone,
  Link,
  Building,
  Zap,
  Calendar,
  History,
  ImageIcon,
  Smartphone,
  Star,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Youtube,
  LinkIcon,
  X,
  KeyRound,
  Copy,
  Info,
  Briefcase,
  UtensilsCrossed,
  Droplets,
  Heart,
  TrendingUp,
  CalendarX,
  CreditCard,
  ArrowLeft,
  ChevronRight,
  Sun,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { companyAdminCustomersAPI, companyAdminApi } from '../../lib/companyAdminApi';
import { useCompanyAdminAuth } from '../../contexts/CompanyAdminAuthContext';
import CompanyAdminLayout from '../../components/CompanyAdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import ThemeToggle from '../../components/ThemeToggle';

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CRON_PRESETS = [
  { label: 'Every minute', value: '* * * * *' },
  { label: 'Every 5 minutes', value: '*/5 * * * *' },
  { label: 'Every 15 minutes', value: '*/15 * * * *' },
  { label: 'Every 30 minutes', value: '*/30 * * * *' },
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Every 2 hours', value: '0 */2 * * *' },
  { label: 'Every day at midnight', value: '0 0 * * *' },
  { label: 'Every day at 6 AM', value: '0 6 * * *' },
  { label: 'Every day at 9 AM', value: '0 9 * * *' },
  { label: 'Every Sunday', value: '0 0 * * 0' },
  { label: 'Every Monday', value: '0 0 * * 1' },
  { label: 'First day of month', value: '0 0 1 * *' },
];

const CATEGORY_COLORS = {
  subscription: 'bg-purple-100 text-purple-700',
  notification: 'bg-blue-100 text-blue-700',
  cleanup: 'bg-orange-100 text-orange-700',
  backup: 'bg-green-100 text-green-700',
  report: 'bg-cyan-100 text-cyan-700',
  sync: 'bg-pink-100 text-pink-700',
};

const CATEGORY_ICONS = {
  subscription: Clock,
  notification: Mail,
  cleanup: FileText,
  backup: Database,
  report: FileText,
  sync: RefreshCw,
};

// Personal Information Tab
const PersonalInfoTab = () => {
  const { user, refreshUser } = useCompanyAdminAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAdminCustomersAPI.updateMyProfile(formData);
      toast.success('Profile updated successfully');
      refreshUser?.();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label>Full Name</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label>Email Address</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label>Phone Number</Label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="mt-1.5"
          />
        </div>
      </div>
      <Button onClick={handleSave} disabled={saving}>
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save Changes'}
      </Button>
    </div>
  );
};

// Security Tab
const SecurityTab = () => {
  const [settings, setSettings] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await companyAdminCustomersAPI.getSecuritySettings();
      setSettings(res.data.security);
    } catch (error) {
      console.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await companyAdminCustomersAPI.updateSecuritySettings(settings);
      toast.success('Security settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    try {
      await companyAdminCustomersAPI.changeMyPassword({
        old_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-8">
      {/* Change Password */}
      <div className="p-6 bg-muted rounded-lg">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5" />
          Change Password
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Current Password</Label>
            <div className="relative mt-1.5">
              <Input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2"
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
              >
                {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label>New Password</Label>
            <div className="relative mt-1.5">
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
              >
                {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label>Confirm Password</Label>
            <div className="relative mt-1.5">
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
              >
                {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        <Button onClick={handleChangePassword} className="mt-4" disabled={saving}>
          {saving ? 'Changing...' : 'Change Password'}
        </Button>
      </div>

      {/* Security Settings */}
      {settings && (
        <div className="p-6 bg-muted rounded-lg">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Security Settings
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center justify-between p-3 bg-card rounded-lg">
              <div>
                <p className="font-medium">Two-Factor Authentication</p>
                <p className="text-sm text-muted-foreground">Require 2FA for login</p>
              </div>
              <Switch
                checked={settings.two_factor_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, two_factor_enabled: v })}
              />
            </div>
            <div>
              <Label>Session Timeout (minutes)</Label>
              <Input
                type="number"
                value={settings.session_timeout_minutes}
                onChange={(e) => setSettings({ ...settings, session_timeout_minutes: parseInt(e.target.value) })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Max Login Attempts</Label>
              <Input
                type="number"
                value={settings.max_login_attempts}
                onChange={(e) => setSettings({ ...settings, max_login_attempts: parseInt(e.target.value) })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Lockout Duration (minutes)</Label>
              <Input
                type="number"
                value={settings.lockout_duration_minutes}
                onChange={(e) => setSettings({ ...settings, lockout_duration_minutes: parseInt(e.target.value) })}
                className="mt-1.5"
              />
            </div>
          </div>
          <Button onClick={handleSaveSettings} className="mt-4" disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Security Settings'}
          </Button>
        </div>
      )}
    </div>
  );
};

// Platform Branding Tab
// Image upload helper component
const ImageUploadField = ({ label, fieldKey, value, onChange, hint }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('field', fieldKey);
      const res = await companyAdminCustomersAPI.uploadBrandingImage(formData);
      onChange(res.data.url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const previewUrl = value
    ? value.startsWith('http')
      ? value
      : `${backendUrl}${value}`
    : null;

  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1.5 flex items-start gap-3">
        {/* Preview box */}
        <div className="w-16 h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted shrink-0 overflow-hidden">
          {previewUrl ? (
            <img src={previewUrl} alt={label} className="w-full h-full object-contain" />
          ) : (
            <ImageIcon className="w-6 h-6 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://... or upload below"
            className="text-sm"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`upload-${fieldKey}`}
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {uploading ? 'Uploading…' : 'Upload File'}
            </Button>
            {value && (
              <Button type="button" variant="ghost" size="sm" onClick={() => onChange('')}>
                <X className="w-3.5 h-3.5 mr-1.5" /> Clear
              </Button>
            )}
          </div>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
        </div>
      </div>
    </div>
  );
};

const AppearanceTab = () => {
  return (
    <div className="space-y-6 max-w-lg" data-testid="appearance-tab">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Appearance</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Choose light, dark, or match your system preference.
        </p>
      </div>
      <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
        <Label className="text-sm font-medium text-foreground">Theme</Label>
        <ThemeToggle showLabels className="w-full sm:w-auto" />
      </div>
    </div>
  );
};

const BrandingTab = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await companyAdminCustomersAPI.getBrandingSettings();
      setSettings(res.data.branding);
    } catch (error) {
      console.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAdminCustomersAPI.updateBrandingSettings(settings);
      toast.success('Branding settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* App Name */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>App Name</Label>
          <Input
            value={settings?.app_name || ''}
            onChange={(e) => setSettings({ ...settings, app_name: e.target.value })}
            className="mt-1.5"
            placeholder="WingsDiet"
          />
        </div>
        <div>
          <Label>Website URL</Label>
          <div className="relative mt-1.5">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={settings?.website_url || ''}
              onChange={(e) => setSettings({ ...settings, website_url: e.target.value })}
              placeholder="https://www.yourdomain.com"
              className="pl-9"
              data-testid="website-url"
            />
          </div>
        </div>
        <div className="md:col-span-2">
          <Label>Admin Panel URL</Label>
          <div className="relative mt-1.5">
            <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={settings?.admin_panel_url || ''}
              onChange={(e) => setSettings({ ...settings, admin_panel_url: e.target.value })}
              placeholder="https://admin.yourdomain.com"
              className="pl-9"
              data-testid="admin-panel-url"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            When this URL is set, the logo, mobile logo and favicon above will be applied across all portals (Company Admin, Partner, Customer).
          </p>
        </div>
      </div>

      {/* Image uploads */}
      <div className="grid grid-cols-1 gap-6">
        <ImageUploadField
          label="App Logo"
          fieldKey="app_logo"
          value={settings?.app_logo}
          onChange={(url) => setSettings({ ...settings, app_logo: url })}
          hint="Recommended: 200×60px PNG or SVG. Shown in the header on desktop."
        />
        <ImageUploadField
          label="Mobile Logo"
          fieldKey="app_logo_mobile"
          value={settings?.app_logo_mobile}
          onChange={(url) => setSettings({ ...settings, app_logo_mobile: url })}
          hint="Recommended: 60×60px PNG or SVG. Used in the mobile navigation header."
        />
        <ImageUploadField
          label="Favicon"
          fieldKey="app_favicon"
          value={settings?.app_favicon}
          onChange={(url) => setSettings({ ...settings, app_favicon: url })}
          hint="Recommended: 32×32px ICO or PNG. Shown in the browser tab."
        />
      </div>

      {/* Colors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label>Primary Color</Label>
          <div className="flex gap-2 mt-1.5">
            <Input
              type="color"
              value={settings?.primary_color || '#3b82f6'}
              onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
              className="w-14 h-10 p-1"
            />
            <Input
              value={settings?.primary_color || '#3b82f6'}
              onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
              className="flex-1"
            />
          </div>
        </div>
        <div>
          <Label>Secondary Color</Label>
          <div className="flex gap-2 mt-1.5">
            <Input
              type="color"
              value={settings?.secondary_color || '#10b981'}
              onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
              className="w-14 h-10 p-1"
            />
            <Input
              value={settings?.secondary_color || '#10b981'}
              onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
              className="flex-1"
            />
          </div>
        </div>
        <div>
          <Label>Support Email</Label>
          <Input
            type="email"
            value={settings?.support_email || ''}
            onChange={(e) => setSettings({ ...settings, support_email: e.target.value })}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label>Support Phone</Label>
          <Input
            value={settings?.support_phone || ''}
            onChange={(e) => setSettings({ ...settings, support_phone: e.target.value })}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label>Terms of Service URL</Label>
          <Input
            value={settings?.terms_url || ''}
            onChange={(e) => setSettings({ ...settings, terms_url: e.target.value })}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label>Privacy Policy URL</Label>
          <Input
            value={settings?.privacy_url || ''}
            onChange={(e) => setSettings({ ...settings, privacy_url: e.target.value })}
            className="mt-1.5"
          />
        </div>
      </div>
      <div>
        <Label>Company Address</Label>
        <Textarea
          value={settings?.company_address || ''}
          onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
          className="mt-1.5"
          rows={3}
        />
      </div>
      <Button onClick={handleSave} disabled={saving} data-testid="save-branding-btn">
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save Branding Settings'}
      </Button>
    </div>
  );
};

// CAPTCHA Tab
const CaptchaTab = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await companyAdminCustomersAPI.getCaptchaSettings();
      setSettings(res.data.captcha);
    } catch (error) {
      console.error('Failed to load CAPTCHA settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAdminCustomersAPI.updateCaptchaSettings(settings);
      toast.success('CAPTCHA settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
        <div>
          <p className="font-medium">Enable CAPTCHA</p>
          <p className="text-sm text-muted-foreground">Protect forms from bots and spam</p>
        </div>
        <Switch
          checked={settings?.enabled || false}
          onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
        />
      </div>

      {settings?.enabled && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>CAPTCHA Provider</Label>
              <Select
                value={settings?.provider || 'recaptcha'}
                onValueChange={(v) => setSettings({ ...settings, provider: v })}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recaptcha">Google reCAPTCHA</SelectItem>
                  <SelectItem value="hcaptcha">hCaptcha</SelectItem>
                  <SelectItem value="turnstile">Cloudflare Turnstile</SelectItem>
                  <SelectItem value="match_captcha">Match CAPTCHA (Self-hosted)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {settings?.provider !== 'match_captcha' && (
              <>
                <div>
                  <Label>Site Key</Label>
                  <Input
                    value={settings?.site_key || ''}
                    onChange={(e) => setSettings({ ...settings, site_key: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Secret Key</Label>
                  <Input
                    type="password"
                    value={settings?.secret_key || ''}
                    onChange={(e) => setSettings({ ...settings, secret_key: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
              </>
            )}
          </div>

          {/* Match CAPTCHA specific settings */}
          {settings?.provider === 'match_captcha' && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-blue-600" />
                <h4 className="font-medium text-blue-800">Match CAPTCHA Configuration</h4>
              </div>
              <p className="text-sm text-blue-700">
                Users are presented with a grid of images and must select those matching a description. No external API keys required.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Challenge Question</Label>
                  <Input
                    value={settings?.match_captcha_question || 'Select all images matching the description'}
                    onChange={(e) => setSettings({ ...settings, match_captcha_question: e.target.value })}
                    className="mt-1.5"
                    placeholder="Select all images that show..."
                  />
                </div>
                <div>
                  <Label>Images in Grid</Label>
                  <Select
                    value={String(settings?.match_captcha_images_count || 6)}
                    onValueChange={(v) => setSettings({ ...settings, match_captcha_images_count: Number(v) })}
                  >
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4 images (2×2)</SelectItem>
                      <SelectItem value="6">6 images (2×3)</SelectItem>
                      <SelectItem value="9">9 images (3×3)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Difficulty / Sensitivity</Label>
                  <Select
                    value={settings?.match_captcha_sensitivity || 'medium'}
                    onValueChange={(v) => setSettings({ ...settings, match_captcha_sensitivity: v })}
                  >
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low — easy for users</SelectItem>
                      <SelectItem value="medium">Medium — balanced</SelectItem>
                      <SelectItem value="high">High — strict matching</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-3">Enable CAPTCHA on:</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span>Login Page</span>
                <Switch
                  checked={settings?.enable_on_login || false}
                  onCheckedChange={(v) => setSettings({ ...settings, enable_on_login: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <span>Registration Page</span>
                <Switch
                  checked={settings?.enable_on_register || false}
                  onCheckedChange={(v) => setSettings({ ...settings, enable_on_register: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <span>Forgot Password Page</span>
                <Switch
                  checked={settings?.enable_on_forgot_password || false}
                  onCheckedChange={(v) => setSettings({ ...settings, enable_on_forgot_password: v })}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <Button onClick={handleSave} disabled={saving}>
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save CAPTCHA Settings'}
      </Button>
    </div>
  );
};

// FTP Backup Tab
const FtpTab = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await companyAdminCustomersAPI.getFtpSettings();
      setSettings(res.data.ftp);
    } catch (error) {
      console.error('Failed to load FTP settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAdminCustomersAPI.updateFtpSettings(settings);
      toast.success('FTP settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      await companyAdminCustomersAPI.testFtpConnection({
        host: settings?.host,
        port: settings?.port,
        username: settings?.username,
        password: settings?.password,
      });
      toast.success('FTP connection successful');
    } catch (error) {
      toast.error('FTP connection failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
        <div>
          <p className="font-medium">Enable FTP Backup</p>
          <p className="text-sm text-muted-foreground">Automatically backup data to FTP server</p>
        </div>
        <Switch
          checked={settings?.enabled || false}
          onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
        />
      </div>

      {settings?.enabled && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Protocol</Label>
              <Select
                value={settings?.protocol || 'ftp'}
                onValueChange={(v) => setSettings({ ...settings, protocol: v })}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ftp">FTP</SelectItem>
                  <SelectItem value="sftp">SFTP</SelectItem>
                  <SelectItem value="ftps">FTPS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Host</Label>
              <Input
                value={settings?.host || ''}
                onChange={(e) => setSettings({ ...settings, host: e.target.value })}
                className="mt-1.5"
                placeholder="ftp.example.com"
              />
            </div>
            <div>
              <Label>Port</Label>
              <Input
                type="number"
                value={settings?.port || 21}
                onChange={(e) => setSettings({ ...settings, port: parseInt(e.target.value) })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Username</Label>
              <Input
                value={settings?.username || ''}
                onChange={(e) => setSettings({ ...settings, username: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={settings?.password || ''}
                onChange={(e) => setSettings({ ...settings, password: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Remote Path</Label>
              <Input
                value={settings?.remote_path || '/backups'}
                onChange={(e) => setSettings({ ...settings, remote_path: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Backup Frequency</Label>
              <Select
                value={settings?.backup_frequency || 'daily'}
                onValueChange={(v) => setSettings({ ...settings, backup_frequency: v })}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Retention Days</Label>
              <Input
                type="number"
                value={settings?.retention_days || 30}
                onChange={(e) => setSettings({ ...settings, retention_days: parseInt(e.target.value) })}
                className="mt-1.5"
              />
            </div>
          </div>

          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-3">Include in Backup:</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span>Database</span>
                <Switch
                  checked={settings?.include_database || false}
                  onCheckedChange={(v) => setSettings({ ...settings, include_database: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <span>Uploads Folder</span>
                <Switch
                  checked={settings?.include_uploads || false}
                  onCheckedChange={(v) => setSettings({ ...settings, include_uploads: v })}
                />
              </div>
            </div>
          </div>

          {settings?.last_backup && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm">
                <strong>Last Backup:</strong> {formatDateTime(settings.last_backup)}
                {settings.last_backup_status && (
                  <span className={cn('ml-2', settings.last_backup_status === 'success' ? 'text-green-600' : 'text-red-600')}>
                    ({settings.last_backup_status})
                  </span>
                )}
              </p>
            </div>
          )}
        </>
      )}

      <div className="flex gap-2">
        {settings?.enabled && (
          <Button variant="outline" onClick={handleTestConnection} disabled={testing}>
            {testing ? 'Testing...' : 'Test Connection'}
          </Button>
        )}
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save FTP Settings'}
        </Button>
      </div>
    </div>
  );
};

// Schedulers Tab
const SchedulersTab = () => {
  const [schedulers, setSchedulers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [selectedScheduler, setSelectedScheduler] = useState(null);
  const [logs, setLogs] = useState([]);
  const [editForm, setEditForm] = useState({ cron_expression: '', description: '' });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadSchedulers();
  }, []);

  const loadSchedulers = async () => {
    try {
      const res = await companyAdminCustomersAPI.getSchedulers();
      setSchedulers(res.data.schedulers || []);
    } catch (error) {
      toast.error('Failed to load schedulers');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (scheduler) => {
    try {
      await companyAdminCustomersAPI.toggleScheduler(scheduler.scheduler_id);
      toast.success(`Scheduler ${scheduler.is_enabled ? 'disabled' : 'enabled'}`);
      loadSchedulers();
    } catch (error) {
      toast.error('Failed to toggle scheduler');
    }
  };

  const handleRunNow = async (scheduler) => {
    setActionLoading(true);
    try {
      await companyAdminCustomersAPI.runScheduler(scheduler.scheduler_id);
      toast.success('Scheduler execution started');
      loadSchedulers();
    } catch (error) {
      toast.error('Failed to run scheduler');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenEdit = (scheduler) => {
    setSelectedScheduler(scheduler);
    setEditForm({
      cron_expression: scheduler.cron_expression,
      description: scheduler.description,
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedScheduler) return;
    setActionLoading(true);
    try {
      await companyAdminCustomersAPI.updateScheduler(selectedScheduler.scheduler_id, editForm);
      toast.success('Scheduler updated');
      setEditModalOpen(false);
      loadSchedulers();
    } catch (error) {
      toast.error('Failed to update scheduler');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewLogs = async (scheduler) => {
    setSelectedScheduler(scheduler);
    setLogsModalOpen(true);
    try {
      const res = await companyAdminCustomersAPI.getSchedulerLogs(scheduler.scheduler_id, { limit: 20 });
      setLogs(res.data.logs || []);
    } catch (error) {
      toast.error('Failed to load logs');
    }
  };

  // Group schedulers by category
  const groupedSchedulers = schedulers.reduce((acc, s) => {
    if (!acc[s.category]) acc[s.category] = [];
    acc[s.category].push(s);
    return acc;
  }, {});

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground">Configure automated tasks and background jobs</p>
        <Button variant="outline" size="sm" onClick={loadSchedulers}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {Object.entries(groupedSchedulers).map(([category, items]) => {
        const CategoryIcon = CATEGORY_ICONS[category] || Clock;
        return (
          <div key={category} className="space-y-3">
            <h3 className="text-lg font-semibold capitalize flex items-center gap-2">
              <CategoryIcon className="w-5 h-5" />
              {category} Tasks
            </h3>
            <div className="space-y-2">
              {items.map((scheduler) => (
                <div
                  key={scheduler.scheduler_id}
                  className={cn(
                    'p-4 bg-card border rounded-lg',
                    !scheduler.is_enabled && 'opacity-60'
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{scheduler.name}</h4>
                        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', CATEGORY_COLORS[category])}>
                          {category}
                        </span>
                        {scheduler.last_status && (
                          <span className={cn(
                            'px-2 py-0.5 rounded-full text-xs font-medium',
                            scheduler.last_status === 'success' ? 'bg-green-100 text-green-700' :
                            scheduler.last_status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          )}>
                            {scheduler.last_status}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{scheduler.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <code className="bg-muted px-1 rounded">{scheduler.cron_expression}</code>
                        </span>
                        {scheduler.last_run && (
                          <span>Last run: {formatDateTime(scheduler.last_run)}</span>
                        )}
                        <span>Runs: {scheduler.run_count || 0}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewLogs(scheduler)}
                        title="View Logs"
                      >
                        <History className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEdit(scheduler)}
                        title="Edit"
                      >
                        <SettingsIcon className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRunNow(scheduler)}
                        disabled={actionLoading || !scheduler.is_enabled}
                        title="Run Now"
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                      <Switch
                        checked={scheduler.is_enabled}
                        onCheckedChange={() => handleToggle(scheduler)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Scheduler</DialogTitle>
            <DialogDescription>{selectedScheduler?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Cron Expression</Label>
              <Select
                value={editForm.cron_expression}
                onValueChange={(v) => setEditForm({ ...editForm, cron_expression: v })}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CRON_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label} ({p.value})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={editForm.cron_expression}
                onChange={(e) => setEditForm({ ...editForm, cron_expression: e.target.value })}
                className="mt-2"
                placeholder="Custom cron expression"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="mt-1.5"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={actionLoading}>
              {actionLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logs Modal */}
      <Dialog open={logsModalOpen} onOpenChange={setLogsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[75vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Scheduler Logs</DialogTitle>
            <DialogDescription>{selectedScheduler?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {logs.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No logs available</p>
            ) : (
              logs.map((log, idx) => (
                <div key={idx} className="p-3 bg-muted rounded-lg text-sm">
                  <div className="flex items-center justify-between">
                    <span className={cn(
                      'px-2 py-0.5 rounded-full text-xs font-medium',
                      log.status === 'success' ? 'bg-green-100 text-green-700' :
                      log.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    )}>
                      {log.status}
                    </span>
                    <span className="text-muted-foreground">{formatDateTime(log.started_at)}</span>
                  </div>
                  {log.duration_ms && (
                    <p className="text-xs text-muted-foreground mt-1">Duration: {log.duration_ms}ms</p>
                  )}
                  {log.error_message && (
                    <p className="text-xs text-red-600 mt-1">{log.error_message}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// URLs Tab
const UrlsTab = () => {
  const [settings, setSettings] = useState(null);
  const [aiRating, setAiRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingRating, setSavingRating] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const urlsRes = await companyAdminCustomersAPI.getUrlSettings();
      setSettings(urlsRes.data.urls);
    } catch { toast.error('Failed to load URL settings'); }
    try {
      const ratingRes = await companyAdminCustomersAPI.getAiAppRatingSettings();
      setAiRating(ratingRes.data.ai_app_rating);
    } catch { /* optional tab */ }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAdminCustomersAPI.updateUrlSettings(settings);
      toast.success('URL settings updated');
    } catch { toast.error('Failed to update settings'); }
    finally { setSaving(false); }
  };

  const handleSaveAiRating = async () => {
    if (!aiRating) return;
    setSavingRating(true);
    try {
      await companyAdminCustomersAPI.updateAiAppRatingSettings(aiRating);
      toast.success('AI app rating settings saved');
    } catch { toast.error('Failed to save AI app rating settings'); }
    finally { setSavingRating(false); }
  };

  const updateSocial = (key, val) =>
    setSettings({ ...settings, social_media: { ...(settings?.social_media || {}), [key]: val } });

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading...</div>;

  const sm = settings?.social_media || {};

  return (
    <div className="space-y-6" data-testid="urls-tab">
      {/* App Store URLs */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Smartphone className="w-4 h-4" /> App Download Links
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Android App URL (Play Store)</Label>
            <div className="relative mt-1.5">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={settings?.android_app_url || ''}
                onChange={(e) => setSettings({ ...settings, android_app_url: e.target.value })}
                placeholder="https://play.google.com/store/apps/details?id=..."
                className="pl-9"
                data-testid="android-app-url"
              />
            </div>
          </div>
          <div>
            <Label>iOS App URL (App Store)</Label>
            <div className="relative mt-1.5">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={settings?.app_store_url || ''}
                onChange={(e) => setSettings({ ...settings, app_store_url: e.target.value })}
                placeholder="https://apps.apple.com/app/..."
                className="pl-9"
                data-testid="app-store-url"
              />
            </div>
          </div>
        </div>
      </div>

      {/* In-chat app rating (AI use counters) */}
      <div className="border-t border-border pt-6">
        <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
          <Star className="w-4 h-4" /> AI chat — app rating prompt
        </h3>
        <p className="text-xs text-muted-foreground mb-4 max-w-3xl">
          After enough <strong>AI uses</strong> (AI chat messages, food bank add-to-diet, manual food create, moderate relax updates, quick log meal), customers who have not rated yet see a 5-star prompt in AI chat.
          Reminders repeat until they rate or the max prompts limit is reached. Store links above apply for 4–5 stars.
        </p>
        {aiRating && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
            <div>
              <Label>First prompt after AI uses</Label>
              <Input
                type="number"
                min={1}
                className="mt-1.5"
                value={aiRating.first_after_ai_uses}
                onChange={(e) => setAiRating({ ...aiRating, first_after_ai_uses: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label>Reminder every (additional AI uses)</Label>
              <Input
                type="number"
                min={1}
                className="mt-1.5"
                value={aiRating.reminder_every_ai_uses}
                onChange={(e) => setAiRating({ ...aiRating, reminder_every_ai_uses: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label>Max prompts per customer</Label>
              <Input
                type="number"
                min={1}
                className="mt-1.5"
                value={aiRating.max_prompts}
                onChange={(e) => setAiRating({ ...aiRating, max_prompts: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label>Min days between prompts (0 = no wait)</Label>
              <Input
                type="number"
                min={0}
                className="mt-1.5"
                value={aiRating.min_days_between_prompts}
                onChange={(e) => setAiRating({ ...aiRating, min_days_between_prompts: Number(e.target.value) })}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Message (use {"{{name}}"} or {"{{customer_name}}"})</Label>
              <Textarea
                className="mt-1.5 min-h-[100px]"
                value={aiRating.message_text || ''}
                onChange={(e) => setAiRating({ ...aiRating, message_text: e.target.value })}
              />
            </div>
          </div>
        )}
        <Button
          type="button"
          variant="secondary"
          className="mt-4"
          onClick={handleSaveAiRating}
          disabled={savingRating || !aiRating}
        >
          <Save className="w-4 h-4 mr-2" />
          {savingRating ? 'Saving...' : 'Save app rating prompt settings'}
        </Button>
      </div>

      {/* Social Media Links */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Globe className="w-4 h-4" /> Social Media Links
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/yourpage', icon: <Facebook className="w-4 h-4 text-blue-600" /> },
            { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourhandle', icon: <Instagram className="w-4 h-4 text-pink-500" /> },
            { key: 'twitter', label: 'Twitter / X', placeholder: 'https://twitter.com/yourhandle', icon: <Twitter className="w-4 h-4 text-sky-500" /> },
            { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/...', icon: <Linkedin className="w-4 h-4 text-blue-700" /> },
            { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/c/...', icon: <Youtube className="w-4 h-4 text-red-600" /> },
            { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@yourhandle', icon: <Link className="w-4 h-4 text-gray-800" /> },
            { key: 'snapchat', label: 'Snapchat', placeholder: 'https://snapchat.com/add/...', icon: <Star className="w-4 h-4 text-yellow-500" /> },
            { key: 'pinterest', label: 'Pinterest', placeholder: 'https://pinterest.com/...', icon: <LinkIcon className="w-4 h-4 text-red-500" /> },
          ].map(({ key, label, placeholder, icon }) => (
            <div key={key}>
              <Label>{label}</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2">{icon}</span>
                <Input
                  value={sm[key] || ''}
                  onChange={(e) => updateSocial(key, e.target.value)}
                  placeholder={placeholder}
                  className="pl-9"
                  data-testid={`social-${key}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} data-testid="save-urls-btn">
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save URL Settings'}
      </Button>
    </div>
  );
};

// Login Authenticator Tab
const LoginAuthTab = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const appleCallbackUrl = `${API_URL}/api/v1/public/apple-callback`;

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await companyAdminCustomersAPI.getLoginAuthSettings();
      setSettings(res.data.login_auth);
    } catch {
      toast.error('Failed to load login auth settings');
    } finally {
      setLoading(false);
    }
  };

  const update = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAdminCustomersAPI.updateLoginAuthSettings(settings);
      toast.success('Login authenticator settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-8" data-testid="login-auth-tab">

      {/* ── Google Login ── */}
      <div className="rounded-xl border border-border p-5 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Google G icon */}
            <div className="w-9 h-9 rounded-lg bg-card border border-border flex items-center justify-center shadow-sm">
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Google Login</h3>
              <p className="text-xs text-muted-foreground">Allow customers to sign in with their Google account</p>
            </div>
          </div>
          <Switch
            data-testid="google-login-enabled"
            checked={!!settings?.google_login_enabled}
            onCheckedChange={(v) => update('google_login_enabled', v)}
          />
        </div>

        <div className={cn('space-y-4 transition-opacity', !settings?.google_login_enabled && 'opacity-40 pointer-events-none')}>
          {settings?.google_client_id ? (
            <div className="p-3 bg-green-50 border border-green-100 rounded-lg flex gap-2 text-xs text-green-700">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Using your <strong>custom Google credentials</strong>. Customers sign in via your Google Cloud project.
                Add <code className="bg-green-100 px-1 rounded">{window.location.origin}</code> as an authorized JavaScript origin in Google Cloud Console.
              </span>
            </div>
          ) : (
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg flex gap-2 text-xs text-blue-700">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                No custom credentials set — Google Sign-in uses <strong>Emergent Auth</strong> (works out of the box).
                To use your own Google Cloud project, enter your Client ID below.
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Google Client ID</Label>
              <Input
                data-testid="google-client-id"
                value={settings?.google_client_id || ''}
                onChange={(e) => update('google_client_id', e.target.value)}
                placeholder="your-client-id.apps.googleusercontent.com"
                className="mt-1.5"
              />
              <p className="text-xs text-muted-foreground mt-1">Found in Google Cloud Console → APIs &amp; Services → Credentials</p>
            </div>
            <div>
              <Label>Google Client Secret</Label>
              <div className="relative mt-1.5">
                <Input
                  data-testid="google-client-secret"
                  type={showSecret ? 'text' : 'password'}
                  value={settings?.google_client_secret || ''}
                  onChange={(e) => update('google_client_secret', e.target.value)}
                  placeholder="GOCSPX-..."
                  className="pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Apple Login ── */}
      <div className="rounded-xl border border-border p-5 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-black flex items-center justify-center shadow-sm">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="white">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Apple Login</h3>
              <p className="text-xs text-muted-foreground">Allow customers to sign in with their Apple ID</p>
            </div>
          </div>
          <Switch
            data-testid="apple-login-enabled"
            checked={!!settings?.apple_login_enabled}
            onCheckedChange={(v) => update('apple_login_enabled', v)}
          />
        </div>

        <div className={cn('space-y-4 transition-opacity', !settings?.apple_login_enabled && 'opacity-40 pointer-events-none')}>

          {/* Redirect URI (read-only info) */}
          <div>
            <Label className="flex items-center gap-1.5">
              Apple Callback / Redirect URI
              <span className="text-xs text-muted-foreground font-normal">(register this in Apple Developer portal)</span>
            </Label>
            <div className="relative mt-1.5">
              <Input
                readOnly
                value={settings?.apple_redirect_uri || appleCallbackUrl}
                className="pr-10 bg-muted text-muted-foreground font-mono text-xs"
                data-testid="apple-redirect-uri"
              />
              <button
                type="button"
                onClick={() => copyToClipboard(settings?.apple_redirect_uri || appleCallbackUrl)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                title="Copy"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Add this URL under your Service ID's redirect URIs in the Apple Developer Console.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Service ID <span className="text-red-500">*</span></Label>
              <Input
                data-testid="apple-service-id"
                value={settings?.apple_service_id || ''}
                onChange={(e) => update('apple_service_id', e.target.value)}
                placeholder="com.yourcompany.wingsdiet.web"
                className="mt-1.5 font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">Found under Identifiers → Service IDs</p>
            </div>
            <div>
              <Label>Team ID <span className="text-red-500">*</span></Label>
              <Input
                data-testid="apple-team-id"
                value={settings?.apple_team_id || ''}
                onChange={(e) => update('apple_team_id', e.target.value)}
                placeholder="ABC123DEF4"
                className="mt-1.5 font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">Top-right corner of your Apple Developer account</p>
            </div>
            <div>
              <Label>Key ID <span className="text-red-500">*</span></Label>
              <Input
                data-testid="apple-key-id"
                value={settings?.apple_key_id || ''}
                onChange={(e) => update('apple_key_id', e.target.value)}
                placeholder="XYZABC1234"
                className="mt-1.5 font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">Found under Certificates → Keys</p>
            </div>
            <div>
              <Label>Custom Redirect URI <span className="text-xs text-muted-foreground">(leave blank to use default above)</span></Label>
              <Input
                data-testid="apple-custom-redirect"
                value={settings?.apple_redirect_uri || ''}
                onChange={(e) => update('apple_redirect_uri', e.target.value)}
                placeholder={appleCallbackUrl}
                className="mt-1.5"
              />
            </div>
          </div>

          <div>
            <Label className="flex items-center justify-between">
              Private Key (.p8 content) <span className="text-red-500">*</span>
              <button
                type="button"
                onClick={() => setShowPrivateKey(v => !v)}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                {showPrivateKey ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {showPrivateKey ? 'Hide' : 'Show'}
              </button>
            </Label>
            <Textarea
              data-testid="apple-private-key"
              value={showPrivateKey ? (settings?.apple_private_key || '') : (settings?.apple_private_key ? '••••••••••••••••••••••••••••••••' : '')}
              onChange={(e) => showPrivateKey && update('apple_private_key', e.target.value)}
              placeholder="-----BEGIN PRIVATE KEY-----&#10;MIGTAgEAMBM...&#10;-----END PRIVATE KEY-----"
              className="mt-1.5 font-mono text-xs resize-none"
              rows={4}
              readOnly={!showPrivateKey}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Paste the full contents of the .p8 file downloaded from Apple Developer → Keys. This is only downloadable once.
            </p>
          </div>

          {/* Setup guide */}
          <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700 space-y-1.5">
            <p className="font-semibold flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Apple Developer Setup Checklist</p>
            <ol className="list-decimal list-inside space-y-1 ml-1">
              <li>Go to <strong>developer.apple.com</strong> → Certificates, Identifiers & Profiles</li>
              <li>Create an <strong>App ID</strong> with "Sign In with Apple" enabled</li>
              <li>Create a <strong>Service ID</strong> (this is your client_id), link it to the App ID</li>
              <li>Under the Service ID, add your <strong>Redirect URI</strong> (copy from above)</li>
              <li>Create a <strong>Key</strong> with "Sign In with Apple" enabled → download the .p8 file</li>
              <li>Fill in all fields above and click Save</li>
            </ol>
          </div>
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} data-testid="save-login-auth-btn">
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save Login Settings'}
      </Button>
    </div>
  );
};

// ── Mailer Tab ────────────────────────────────────────────────────────────────

const EVENT_TYPE_LABELS = {
  customer_registration: 'Customer Registration',
  customer_welcome: 'Customer Welcome',
  partner_registration: 'Partner Registration',
  partner_commission_earned: 'Commission Earned',
  partner_withdrawal_approved: 'Withdrawal Approved',
  partner_withdrawal_rejected: 'Withdrawal Rejected',
  reseller_registration: 'Reseller Registration',
  subscription_activated: 'Subscription Activated',
  subscription_expired: 'Subscription Expired',
  password_reset: 'Password Reset',
  referral_success: 'Referral Success',
  AFTER_SIGNUP: 'After Signup',
  INACTIVITY: 'Inactivity',
  INTERVAL: 'Interval',
  DAILY: 'Daily',
  MEAL_TIME: 'Meal Time',
  ONE_TIME: 'One Time',
  FOOD_EXPIRE: 'Food Expire',
  MEMBERSHIP_EXPIRED: 'Membership Expired',
  HEALTH_INTAKE: 'Health (food log)',
  INACTIVITY_3_DAYS: '3-Day Inactivity',
  AI_MEMBERSHIP_EXPIRED: 'AI Membership Expired',
  DIET_INTAKE_REMINDER: 'Diet Intake Reminder',
  AI_FIRST_USE_APP_RATING: 'First AI — App rating',
};

const EVENT_TYPE_COLORS = {
  customer_registration: '#f97316',
  customer_welcome: '#f97316',
  partner_registration: '#2563eb',
  partner_commission_earned: '#2563eb',
  partner_withdrawal_approved: '#16a34a',
  partner_withdrawal_rejected: '#dc2626',
  reseller_registration: '#7c3aed',
  subscription_activated: '#0891b2',
  subscription_expired: '#b45309',
  password_reset: '#6b7280',
  referral_success: '#0f3460',
  AFTER_SIGNUP: '#16a34a',
  INACTIVITY: '#f97316',
  INTERVAL: '#2563eb',
  DAILY: '#7c3aed',
  MEAL_TIME: '#0891b2',
  ONE_TIME: '#b45309',
  FOOD_EXPIRE: '#dc2626',
  MEMBERSHIP_EXPIRED: '#6b7280',
  HEALTH_INTAKE: '#e11d48',
  INACTIVITY_3_DAYS: '#ea580c',
  AI_MEMBERSHIP_EXPIRED: '#ca8a04',
  DIET_INTAKE_REMINDER: '#f97316',
  AI_FIRST_USE_APP_RATING: '#d97706',
};

// SMTP sub-tab
const SmtpSettingsPanel = () => {
  const [form, setForm] = useState({
    host: '', port: 587, username: '', password: '',
    from_email: '', from_name: '', encryption: 'tls', enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showPass, setShowPass] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await companyAdminCustomersAPI.getSmtpSettings();
      setForm(res.data);
    } catch { toast.error('Failed to load SMTP settings'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAdminCustomersAPI.updateSmtpSettings(form);
      toast.success('SMTP settings saved');
    } catch { toast.error('Failed to save SMTP settings'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!testEmail) { toast.error('Enter a test email address'); return; }
    setTesting(true);
    try {
      await companyAdminCustomersAPI.testSmtpConnection({ test_email: testEmail });
      toast.success('Test email sent! Check your inbox.');
    } catch (e) {
      toast.error(e.response?.data?.message || 'SMTP test failed');
    } finally { setTesting(false); }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      {/* Enable toggle */}
      <div
        style={{ background: form.enabled ? 'linear-gradient(135deg, #1a1a2e 0%, #1e3a8a 100%)' : '#f8fafc' }}
        className="rounded-xl p-5 flex items-center justify-between border border-border transition-all"
      >
        <div>
          <p className={`font-semibold text-base ${form.enabled ? 'text-white' : 'text-foreground'}`}>SMTP Email Delivery</p>
          <p className={`text-sm mt-0.5 ${form.enabled ? 'text-blue-200' : 'text-muted-foreground'}`}>
            {form.enabled ? 'Outbound emails are active via your SMTP server' : 'Enable to send transactional emails via your SMTP server'}
          </p>
        </div>
        <Switch
          data-testid="smtp-enabled-toggle"
          checked={form.enabled}
          onCheckedChange={(v) => setForm({ ...form, enabled: v })}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>SMTP Host</Label>
          <Input data-testid="smtp-host" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} className="mt-1.5" placeholder="smtp.gmail.com" />
        </div>
        <div>
          <Label>Port</Label>
          <Input data-testid="smtp-port" type="number" value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} className="mt-1.5" />
        </div>
        <div>
          <Label>Username</Label>
          <Input data-testid="smtp-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1.5" placeholder="you@gmail.com" />
        </div>
        <div>
          <Label>Password</Label>
          <div className="relative mt-1.5">
            <Input
              data-testid="smtp-password"
              type={showPass ? 'text' : 'password'}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="App password or SMTP password"
              className="pr-10"
            />
            <button type="button" onClick={() => setShowPass(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label>From Email</Label>
          <Input data-testid="smtp-from-email" type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} className="mt-1.5" placeholder="no-reply@yourdomain.com" />
        </div>
        <div>
          <Label>From Name</Label>
          <Input data-testid="smtp-from-name" value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} className="mt-1.5" placeholder="WingsDiet" />
        </div>
        <div>
          <Label>Encryption</Label>
          <Select value={form.encryption} onValueChange={(v) => setForm({ ...form, encryption: v })}>
            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tls">TLS (port 587)</SelectItem>
              <SelectItem value="ssl">SSL (port 465)</SelectItem>
              <SelectItem value="none">None (port 25)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button onClick={handleSave} disabled={saving} data-testid="save-smtp-btn" style={{ background: 'linear-gradient(135deg, #1a1a2e, #2563eb)', color: '#fff', border: 'none' }}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving…' : 'Save SMTP Settings'}
        </Button>
      </div>

      {/* Test Email */}
      <div className="rounded-xl border border-border p-5 space-y-3" style={{ background: '#f8fafc' }}>
        <h4 className="font-semibold text-sm flex items-center gap-2" style={{ color: '#1a1a2e' }}>
          <Zap className="w-4 h-4" style={{ color: '#f97316' }} />
          Send Test Email
        </h4>
        <p className="text-xs text-muted-foreground">Verify your SMTP configuration by sending a test email.</p>
        <div className="flex gap-2">
          <Input
            data-testid="test-email-input"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Enter test email address"
            className="flex-1"
          />
          <Button onClick={handleTest} disabled={testing || !form.enabled} data-testid="send-test-email-btn" variant="outline" style={{ borderColor: '#2563eb', color: '#2563eb' }}>
            {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />}
            {testing ? 'Sending…' : 'Send Test'}
          </Button>
        </div>
        {!form.enabled && <p className="text-xs text-amber-600">Enable SMTP above before sending a test.</p>}
      </div>
    </div>
  );
};

// Email Templates sub-tab
const EmailTemplatesPanel = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', subject: '', body_html: '', enabled: true });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      const res = await companyAdminCustomersAPI.getEmailTemplates();
      setTemplates(res.data.templates || []);
    } catch { toast.error('Failed to load email templates'); }
    finally { setLoading(false); }
  };

  const openEdit = (t) => {
    setEditingId(t._id);
    setEditForm({ name: t.name, subject: t.subject, body_html: t.body_html, enabled: t.enabled });
    setPreview(false);
  };

  const closeEdit = () => { setEditingId(null); setPreview(false); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAdminCustomersAPI.updateEmailTemplate(editingId, editForm);
      toast.success('Template saved');
      setTemplates(prev => prev.map(t => t._id === editingId ? { ...t, ...editForm } : t));
      closeEdit();
    } catch { toast.error('Failed to save template'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (t) => {
    try {
      await companyAdminCustomersAPI.updateEmailTemplate(t._id, { ...t, enabled: !t.enabled });
      setTemplates(prev => prev.map(x => x._id === t._id ? { ...x, enabled: !x.enabled } : x));
    } catch { toast.error('Failed to toggle template'); }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading…</div>;

  const editing = editingId ? templates.find(t => t._id === editingId) : null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Manage the email templates sent for each event. All templates support variables like <code className="bg-muted px-1 rounded text-xs">{'{{customer_name}}'}</code>, <code className="bg-muted px-1 rounded text-xs">{'{{brand_name}}'}</code>, etc.
      </p>

      {/* Template list */}
      <div className="space-y-2">
        {templates.map((t) => {
          const color = EVENT_TYPE_COLORS[t.event_type] || '#2563eb';
          const isOpen = editingId === t._id;
          return (
            <div
              key={t._id}
              data-testid={`template-item-${t.event_type}`}
              className="rounded-xl border border-border overflow-hidden"
              style={{ background: isOpen ? '#fff' : '#fafafa' }}
            >
              {/* Header row */}
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: t.enabled ? color : '#d1d5db' }}
                  />
                  <div>
                    <p className="font-medium text-sm" style={{ color: '#1a1a2e' }}>{t.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t.subject}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className="text-xs font-medium px-2 py-0.5 rounded-full"
                    style={{ background: `${color}18`, color }}
                  >
                    {EVENT_TYPE_LABELS[t.event_type] || t.event_type}
                  </span>
                  <Switch
                    data-testid={`template-toggle-${t.event_type}`}
                    checked={t.enabled}
                    onCheckedChange={() => handleToggle(t)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`template-edit-${t.event_type}`}
                    onClick={() => isOpen ? closeEdit() : openEdit(t)}
                    style={{ color: isOpen ? '#2563eb' : '#6b7280' }}
                  >
                    {isOpen ? <X className="w-4 h-4" /> : <SettingsIcon className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Expanded edit panel */}
              {isOpen && (
                <div className="border-t border-border">
                  {/* Tabs: Edit / Preview */}
                  <div className="flex border-b border-border">
                    <button
                      onClick={() => setPreview(false)}
                      className="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
                      style={{ borderColor: !preview ? '#2563eb' : 'transparent', color: !preview ? '#2563eb' : '#6b7280' }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setPreview(true)}
                      className="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
                      style={{ borderColor: preview ? '#2563eb' : 'transparent', color: preview ? '#2563eb' : '#6b7280' }}
                    >
                      Preview
                    </button>
                  </div>

                  {!preview ? (
                    <div className="p-4 space-y-4">
                      <div>
                        <Label>Template Name</Label>
                        <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1.5" />
                      </div>
                      <div>
                        <Label>Subject Line</Label>
                        <Input
                          data-testid="template-subject-input"
                          value={editForm.subject}
                          onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                          className="mt-1.5"
                          placeholder="Subject with {{variables}}"
                        />
                      </div>
                      <div>
                        <Label>Email Body (HTML)</Label>
                        <Textarea
                          data-testid="template-body-input"
                          value={editForm.body_html}
                          onChange={(e) => setEditForm({ ...editForm, body_html: e.target.value })}
                          className="mt-1.5 font-mono text-xs"
                          rows={8}
                          placeholder="<p>Hi {{customer_name}},</p>"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleSave}
                          disabled={saving}
                          data-testid="save-template-btn"
                          style={{ background: 'linear-gradient(135deg, #1a1a2e, #2563eb)', color: '#fff', border: 'none' }}
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {saving ? 'Saving…' : 'Save Template'}
                        </Button>
                        <Button variant="ghost" onClick={closeEdit}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    /* Live Preview */
                    <div className="p-4">
                      <div className="rounded-xl overflow-hidden border border-border shadow-sm max-w-lg mx-auto">
                        {/* Email header */}
                        <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #1e3a8a 55%, #0f3460 100%)', padding: '24px 28px' }}>
                          <div className="flex items-center gap-2 mb-3">
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Mail size={16} color="#fff" />
                            </div>
                            <span style={{ color: '#93c5fd', fontSize: 12, fontWeight: 600 }}>WingsDiet</span>
                          </div>
                          <p style={{ color: '#fff', fontWeight: 800, fontSize: 18, margin: 0 }}>{editForm.subject || 'Email Subject'}</p>
                        </div>
                        {/* Email body */}
                        <div style={{ background: '#ffffff', padding: '24px 28px' }}>
                          <div
                            style={{ color: '#374151', fontSize: 14, lineHeight: 1.7 }}
                            dangerouslySetInnerHTML={{ __html: editForm.body_html || '<p style="color:#6b7280">Start writing your email body above…</p>' }}
                          />
                        </div>
                        {/* Email footer */}
                        <div style={{ background: '#f8fafc', borderTop: '1px solid #e5e7eb', padding: '16px 28px', textAlign: 'center' }}>
                          <p style={{ color: '#9ca3af', fontSize: 11, margin: 0 }}>
                            Sent by <span style={{ color: '#f97316', fontWeight: 600 }}>WingsDiet</span> · Unsubscribe
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Email Logs sub-tab
const EmailLogsPanel = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await companyAdminCustomersAPI.getEmailLogs({
        page, limit: 20, search, status: statusFilter, event_type: typeFilter,
      });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch { toast.error('Failed to load email logs'); }
    finally { setLoading(false); }
  }, [page, search, statusFilter, typeFilter]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const STATUS_STYLES = {
    sent: { background: '#dcfce7', color: '#16a34a' },
    failed: { background: '#fee2e2', color: '#dc2626' },
    pending: { background: '#fef9c3', color: '#b45309' },
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <AlertCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="log-search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search email or subject…"
            className="pl-9"
          />
        </div>
        <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-36" data-testid="log-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter || 'all'} onValueChange={(v) => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-48" data-testid="log-type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(EVENT_TYPE_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => loadLogs()} data-testid="refresh-logs-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>{total} total {total === 1 ? 'log' : 'logs'}</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-10 text-muted-foreground">Loading…</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 rounded-xl border border-dashed border-border">
          <Mail className="w-8 h-8 mx-auto mb-2 text-muted-foreground opacity-40" />
          <p className="text-sm text-muted-foreground">No email logs found</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #1e3a8a 100%)' }}>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: '#93c5fd' }}>Recipient</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: '#93c5fd' }}>Subject</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: '#93c5fd' }}>Event Type</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: '#93c5fd' }}>Status</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: '#93c5fd' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, idx) => (
                <tr key={log._id} className={idx % 2 === 0 ? 'bg-card' : 'bg-muted/60'}>
                  <td className="px-4 py-3">
                    <p className="font-medium" style={{ color: '#1a1a2e' }}>{log.recipient_name || '—'}</p>
                    <p className="text-xs text-muted-foreground">{log.recipient_email}</p>
                  </td>
                  <td className="px-4 py-3 text-foreground max-w-xs truncate">{log.subject}</td>
                  <td className="px-4 py-3">
                    <span
                      className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={{
                        background: `${EVENT_TYPE_COLORS[log.event_type] || '#2563eb'}18`,
                        color: EVENT_TYPE_COLORS[log.event_type] || '#2563eb',
                      }}
                    >
                      {EVENT_TYPE_LABELS[log.event_type] || log.event_type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={STATUS_STYLES[log.status] || STATUS_STYLES.pending}
                    >
                      {log.status}
                    </span>
                    {log.error && (
                      <p className="text-xs text-red-500 mt-1 max-w-xs truncate" title={log.error}>{log.error}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                    {log.created_at ? new Date(log.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">Page {page} of {pages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Mailer wrapper with inner tabs
const MailerTab = () => {
  const [innerTab, setInnerTab] = useState('smtp');

  const INNER_TABS = [
    { id: 'smtp', label: 'SMTP', icon: Server },
    { id: 'templates', label: 'Email Templates', icon: FileText },
    { id: 'logs', label: 'Email Logs', icon: History },
  ];

  return (
    <div className="space-y-5">
      {/* Inner tab bar */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background: '#f1f5f9' }}>
        {INNER_TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`mailer-inner-tab-${id}`}
            onClick={() => setInnerTab(id)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center"
            style={
              innerTab === id
                ? { background: 'linear-gradient(135deg, #1a1a2e, #2563eb)', color: '#fff', boxShadow: '0 2px 8px rgba(37,99,235,0.25)' }
                : { color: '#6b7280', background: 'transparent' }
            }
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      {innerTab === 'smtp' && <SmtpSettingsPanel />}
      {innerTab === 'templates' && <EmailTemplatesPanel />}
      {innerTab === 'logs' && <EmailLogsPanel />}
    </div>
  );
};

// Main Settings Page

const RolesTab = () => {
  const { companyConfig } = useCompanyAdminAuth();
  const [planData, setPlanData] = React.useState(null);
  const [form, setForm] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await companyAdminApi.get('/admin/company/settings/plans');
        const d = res.data;
        setPlanData(d);
        setForm({
          // Free users
          free_ai_daily_count:            d.free_ai_daily_count            ?? 0,
          free_ai_daily_chat_count:       d.free_ai_daily_chat_count       ?? 0,
          // Trial users
          trial_ai_daily_count:           d.trial_ai_daily_count           ?? 0,
          trial_ai_daily_chat_count:      d.trial_ai_daily_chat_count      ?? 0,
          // Premium Plan
          premium_ai_daily_count:         d.premium_ai_daily_count         ?? 0,
          premium_ai_daily_chat_count:    d.premium_ai_daily_chat_count    ?? 0,
        });
      } catch { toast.error('Failed to load plan settings'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAdminApi.put('/admin/company/settings/plans', form);
      toast.success('Plan settings saved');
    } catch { toast.error('Failed to save plan settings'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const roleCards = [
    {
      key: 'free',
      title: 'Free',
      icon: <User className="w-5 h-5 text-muted-foreground" />,
      description: 'Applied when a customer has no active trial and no premium membership.',
      countField: 'free_ai_daily_count',
      chatField: 'free_ai_daily_chat_count',
      countHint: 'Max AI interactions per day for free users. 0 = unlimited.',
      chatHint: 'Max AI chat messages per day for free users. 0 = unlimited.',
    },
    {
      key: 'trial',
      title: 'Trial',
      icon: <Clock className="w-5 h-5 text-blue-500" />,
      description: 'Applied while a customer is on an active trial.',
      countField: 'trial_ai_daily_count',
      chatField: 'trial_ai_daily_chat_count',
      countHint: 'Max AI interactions per day during the trial. 0 = unlimited.',
      chatHint: 'Max AI chat messages per day during the trial. 0 = unlimited.',
    },
    {
      key: 'premium',
      title: 'Premium',
      icon: <Star className="w-5 h-5 text-yellow-500" />,
      description: 'Applied to active premium members.',
      countField: 'premium_ai_daily_count',
      chatField: 'premium_ai_daily_chat_count',
      countHint: 'Max AI interactions per day for Premium members. 0 = unlimited.',
      chatHint: 'Max AI chat messages per day for Premium members. 0 = unlimited.',
    },
  ];

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h3 className="text-lg font-semibold">Plan Settings</h3>
        <p className="text-sm text-muted-foreground mt-1">Configure daily AI count and AI chat count for each user role.</p>
      </div>

      {roleCards.map((role) => (
        <div key={role.key} className="border border-border rounded-xl p-6 space-y-5">
          <div className="flex items-center gap-2">
            {role.icon}
            <h4 className="font-semibold text-base">{role.title}</h4>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">{role.description}</p>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <Label>AI Daily Count</Label>
              <Input
                type="number"
                min="0"
                value={form[role.countField]}
                onChange={e => setForm(f => ({ ...f, [role.countField]: Number(e.target.value) }))}
                className="mt-1.5"
                placeholder="e.g. 10"
                data-testid={`${role.key}-ai-daily-count`}
              />
              <p className="text-xs text-muted-foreground mt-1">{role.countHint}</p>
            </div>
            <div>
              <Label>AI Daily Chat Count</Label>
              <Input
                type="number"
                min="0"
                value={form[role.chatField]}
                onChange={e => setForm(f => ({ ...f, [role.chatField]: Number(e.target.value) }))}
                className="mt-1.5"
                placeholder="e.g. 5"
                data-testid={`${role.key}-ai-daily-chat-count`}
              />
              <p className="text-xs text-muted-foreground mt-1">{role.chatHint}</p>
            </div>
          </div>
        </div>
      ))}

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-plan-settings">
          {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2 inline-block" /> : <Save className="w-4 h-4 mr-2" />}
          Save Plan Settings
        </Button>
      </div>
    </div>
  );
};


// ============================================================
// EventsTab - Automated Events Engine
// ============================================================

const EVENT_TYPES = [
  { value: "AFTER_SIGNUP", label: "After Signup", description: "Send X minutes after customer registers" },
  { value: "INACTIVITY", label: "Inactivity", description: "Send when customer is inactive for X minutes" },
  { value: "INTERVAL", label: "Interval", description: "Send every X minutes to all customers" },
  { value: "DAILY", label: "Daily", description: "Send once daily at a specific time" },
  { value: "MEAL_TIME", label: "Meal Time", description: "Send at breakfast, lunch, or dinner time" },
  { value: "ONE_TIME", label: "One Time", description: "Send once at a specific date and time" },
  { value: "FOOD_EXPIRE", label: "Food Expire", description: "Send when assigned food plan expires for customer" },
  { value: "MEMBERSHIP_EXPIRED", label: "Membership Expired", description: "Send when customer membership (premium) expires" },
  { value: "HEALTH_INTAKE", label: "Health (food log)", description: "AI checks logged food vs customer health issues when they log intake" },
  { value: "INACTIVITY_3_DAYS", label: "3-day inactivity (midnight)", description: "Daily at 12:00 AM (customer timezone) if no diet log for 3+ days" },
  { value: "AI_MEMBERSHIP_EXPIRED", label: "AI membership expired (midnight)", description: "Daily at 12:00 AM for customers whose AI plan is expired" },
  { value: "DIET_INTAKE_REMINDER", label: "Diet intake reminder", description: "At each customer’s reminder meal time (profile + timezone); requires active AI membership" },
];

const MEAL_TIMES = [
  { value: "breakfast", label: "Breakfast (08:00)" },
  { value: "lunch", label: "Lunch (13:00)" },
  { value: "dinner", label: "Dinner (19:00)" },
];

const defaultElement = () => ({ type: "text", content: "", id: Date.now() });
const defaultButtonElement = () => ({ type: "button", label: "", button_type: "VIEW_PLAN", url: "", id: Date.now() });

const defaultSchedule = (eventType) => {
  switch (eventType) {
    case "AFTER_SIGNUP": return { delay_minutes: 60 };
    case "INACTIVITY": return { delay_minutes: 1440 };
    case "INTERVAL": return { interval_minutes: 60 };
    case "DAILY": return { time_of_day: "08:00" };
    case "MEAL_TIME": return { meal_time: "breakfast" };
    case "ONE_TIME": return { execute_at: new Date(Date.now() + 3600000).toISOString() };
    case "HEALTH_INTAKE": return {};
    case "INACTIVITY_3_DAYS": return { time_of_day: "00:00" };
    case "AI_MEMBERSHIP_EXPIRED": return { time_of_day: "00:00" };
    case "DIET_INTAKE_REMINDER": return {};
    default: return {};
  }
};

const BUTTON_TYPES = ["VIEW_PLAN", "OPEN_URL", "CONFIRM", "DISMISS"];

function ScheduleConfig({ eventType, schedule, onChange }) {
  const upd = (key, val) => onChange({ ...schedule, [key]: val });
  if (eventType === "AFTER_SIGNUP" || eventType === "INACTIVITY") {
    return (
      <div className="space-y-2">
        <Label>Delay (minutes)</Label>
        <Input type="number" min="1" value={schedule.delay_minutes || ""} onChange={e => upd("delay_minutes", Number(e.target.value))} placeholder="e.g. 60" />
        <p className="text-xs text-muted-foreground">
          {eventType === "AFTER_SIGNUP" ? "Message sent X minutes after customer signs up" : "Message sent when customer has been inactive for X minutes"}
        </p>
      </div>
    );
  }
  if (eventType === "INTERVAL") {
    return (
      <div className="space-y-2">
        <Label>Every (minutes)</Label>
        <Input type="number" min="1" value={schedule.interval_minutes || ""} onChange={e => upd("interval_minutes", Number(e.target.value))} placeholder="e.g. 60" />
        <p className="text-xs text-muted-foreground">Message sent every X minutes to all active customers</p>
      </div>
    );
  }
  if (eventType === "DAILY") {
    return (
      <div className="space-y-2">
        <Label>Send at time (HH:MM)</Label>
        <Input type="time" value={schedule.time_of_day || "08:00"} onChange={e => upd("time_of_day", e.target.value)} />
        <p className="text-xs text-muted-foreground">Message sent once per day at this time</p>
      </div>
    );
  }
  if (eventType === "INACTIVITY_3_DAYS") {
    return (
      <div className="space-y-2">
        <Label>Send at time (HH:MM, customer timezone)</Label>
        <Input type="time" value={schedule.time_of_day || "00:00"} onChange={e => upd("time_of_day", e.target.value)} />
        <p className="text-xs text-muted-foreground">
          Runs once per calendar day at this local time. Only customers with no diet intake logged for at least 3 full days receive the message. Default 00:00 is midnight (12:00 AM).
        </p>
      </div>
    );
  }
  if (eventType === "AI_MEMBERSHIP_EXPIRED") {
    return (
      <div className="space-y-2">
        <Label>Send at time (HH:MM, customer timezone)</Label>
        <Input type="time" value={schedule.time_of_day || "00:00"} onChange={e => upd("time_of_day", e.target.value)} />
        <p className="text-xs text-muted-foreground">
          Runs once per calendar day at this local time. Only customers with AI membership inactive (expired or missing expiry while marked as AI plan) receive the message. Default 00:00 is midnight (12:00 AM).
        </p>
      </div>
    );
  }
  if (eventType === "MEAL_TIME") {
    return (
      <div className="space-y-2">
        <Label>Meal Time</Label>
        <Select value={schedule.meal_time || "breakfast"} onValueChange={val => upd("meal_time", val)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {MEAL_TIMES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">Message sent once per day at the selected meal time</p>
      </div>
    );
  }
  if (eventType === "ONE_TIME") {
    // Convert stored UTC to LOCAL time for display; convert local input back to UTC for storage
    const _raw = schedule.execute_at || "";
    const _utcDate = _raw ? new Date(_raw) : null;
    const _pad = (n) => String(n).padStart(2, "0");
    const _dateVal = _utcDate
      ? `${_utcDate.getFullYear()}-${_pad(_utcDate.getMonth()+1)}-${_pad(_utcDate.getDate())}`
      : "";
    const _timeVal = _utcDate
      ? `${_pad(_utcDate.getHours())}:${_pad(_utcDate.getMinutes())}`
      : "";
    const _todayLocal = () => {
      const n = new Date();
      return `${n.getFullYear()}-${_pad(n.getMonth()+1)}-${_pad(n.getDate())}`;
    };
    const _handleDate = (e) => {
      const d = e.target.value;
      if (!d) { upd("execute_at", ""); return; }
      upd("execute_at", new Date(`${d}T${_timeVal || "00:00"}:00`).toISOString());
    };
    const _handleTime = (e) => {
      const t = e.target.value;
      if (!t) { upd("execute_at", ""); return; }
      upd("execute_at", new Date(`${_dateVal || _todayLocal()}T${t}:00`).toISOString());
    };
    return (
      <div className="space-y-2">
        <Label>Send at date &amp; time (your local time)</Label>
        <div className="flex gap-2">
          <Input type="date" value={_dateVal} onChange={_handleDate} className="flex-1" />
          <Input type="time" value={_timeVal} onChange={_handleTime} className="flex-1" />
        </div>
        <p className="text-xs text-muted-foreground">Time is in your local timezone — message fires at exactly this time</p>
      </div>
    );
  }
  if (eventType === "FOOD_EXPIRE") {
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium">Trigger: Food Plan Expiry</p>
        <p className="text-xs text-muted-foreground">Fires automatically on the day a customer's assigned food plan (strict or moderate) expires. No extra configuration needed.</p>
      </div>
    );
  }
  if (eventType === "MEMBERSHIP_EXPIRED") {
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium">Trigger: Membership Expiry</p>
        <p className="text-xs text-muted-foreground">Fires automatically on the day a customer's premium membership expires. No extra configuration needed.</p>
      </div>
    );
  }
  if (eventType === "HEALTH_INTAKE") {
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium">Trigger: Diet intake logged</p>
        <p className="text-xs text-muted-foreground">
          Runs when a customer logs food from any source. Requires an active AI membership, health issues on the profile, AI Chat enabled, and this event active. The chat message is generated by AI (not the blocks below).
        </p>
      </div>
    );
  }
  if (eventType === "DIET_INTAKE_REMINDER") {
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium">Trigger: Customer reminder meal times</p>
        <p className="text-xs text-muted-foreground">
          The engine runs every minute and compares the server clock to each customer&apos;s <strong>Reminder Meal Times</strong> (on their profile) in their <strong>timezone</strong>. Sends only if reminders are enabled, the slot is on, and they have an <strong>active AI membership</strong>. After the opener, AI chat uses an interactive portion flow until the meal is logged. No schedule fields are used.
        </p>
      </div>
    );
  }
  return null;
}

function MessageBuilder({ elements, onChange }) {
  const addText = () => onChange([...elements, defaultElement()]);
  const addButton = () => onChange([...elements, defaultButtonElement()]);
  const removeEl = (idx) => onChange(elements.filter((_, i) => i !== idx));
  const updateEl = (idx, field, val) => {
    const updated = elements.map((el, i) => i === idx ? { ...el, [field]: val } : el);
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label>Message Elements</Label>
        <span className="text-xs text-muted-foreground">Build your message with text and button blocks</span>
      </div>
      {elements.length === 0 && (
        <div className="border-2 border-dashed border-muted rounded-lg p-4 text-center text-muted-foreground text-sm">
          No elements yet. Add a text or button block below.
        </div>
      )}
      <div className="space-y-2">
        {elements.map((el, idx) => (
          <div key={el.id || idx} className="border border-border rounded-lg p-3 bg-muted/20">
            <div className="flex items-center justify-between mb-2">
              <span className={cn("text-xs font-medium px-2 py-0.5 rounded", el.type === "text" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700")}>
                {el.type === "text" ? "Text Block" : "Button Block"}
              </span>
              <button type="button" onClick={() => removeEl(idx)} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
            </div>
            {el.type === "text" ? (
              <Textarea
                value={el.content || ""}
                onChange={e => updateEl(idx, "content", e.target.value)}
                placeholder="Enter message text..."
                rows={3}
              />
            ) : (
              <div className="space-y-2">
                <Input value={el.label || ""} onChange={e => updateEl(idx, "label", e.target.value)} placeholder="Button label (e.g. View My Plan)" />
                <Select value={el.button_type || "VIEW_PLAN"} onValueChange={val => updateEl(idx, "button_type", val)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BUTTON_TYPES.map(bt => <SelectItem key={bt} value={bt}>{bt}</SelectItem>)}
                  </SelectContent>
                </Select>
                {el.button_type === "OPEN_URL" && (
                  <Input value={el.url || ""} onChange={e => updateEl(idx, "url", e.target.value)} placeholder="https://..." />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={addText}>+ Add Text Block</Button>
        <Button type="button" variant="outline" size="sm" onClick={addButton}>+ Add Button Block</Button>
      </div>
    </div>
  );
}

function EventCard({ event, onEdit, onDelete, onToggle, onDryRun }) {
  const typeInfo = EVENT_TYPES.find(t => t.value === event.event_type) || { label: event.event_type };
  const scheduleLabel = (() => {
    const s = event.schedule || {};
    switch(event.event_type) {
      case "AFTER_SIGNUP": return `After ${s.delay_minutes || 60} min`;
      case "INACTIVITY": return `After ${s.delay_minutes || 1440} min inactive`;
      case "INTERVAL": return `Every ${s.interval_minutes || 60} min`;
      case "DAILY": return `Daily at ${s.time_of_day || "08:00"}`;
      case "MEAL_TIME":
        return event.ai_event_category === "DIET_REMINDER"
          ? "Reminder meal times (customer TZ)"
          : `At ${s.meal_time || "breakfast"}`;
      case "FOOD_EXPIRE": return "Food plan expiry day";
      case "MEMBERSHIP_EXPIRED": return "Membership expiry day";
      case "HEALTH_INTAKE": return "When customer logs food";
      case "INACTIVITY_3_DAYS": return `Daily ${s.time_of_day || "00:00"} if 3+ days no log`;
      case "AI_MEMBERSHIP_EXPIRED": return `Daily ${s.time_of_day || "00:00"} if AI plan expired`;
      case "DIET_INTAKE_REMINDER": return "Reminder meal times (customer TZ)";
      case "AI_FIRST_USE_APP_RATING": return "Legacy: first AI rating (deprecated)";
      default: return "-";
    }
  })();
  const elemCount = event.message?.elements?.length || 0;
  return (
    <div className={cn("border rounded-lg p-4 transition-all", event.is_active ? "border-border bg-card" : "border-dashed border-muted bg-muted/20")}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-sm truncate">{event.name}</h4>
            <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", event.is_active ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground")}>
              {event.is_active ? "Active" : "Inactive"}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
            {event.ai_event_category && (() => {
              const cat = AI_EVENT_CATEGORIES.find(c => c.value === event.ai_event_category);
              if (!cat) return null;
              const Icon = cat.icon;
              return (
                <span className={cn('flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border', cat.border, cat.bg, cat.color)}>
                  <Icon className="w-2.5 h-2.5" />{cat.label}
                </span>
              );
            })()}
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3" />{typeInfo.label}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />{scheduleLabel}
            </span>
            <span className="flex items-center gap-1">
              <FileText className="w-3 h-3" />{elemCount} element{elemCount !== 1 ? "s" : ""}
            </span>
            {event.execution_count > 0 && (
              <span className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3 text-green-500" />{event.execution_count} sent
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            title={event.is_active ? "Disable event" : "Enable event"}
            onClick={() => onToggle(event)}
            className={cn("p-1.5 rounded hover:bg-muted transition-colors", event.is_active ? "text-green-600 hover:text-green-700" : "text-muted-foreground hover:text-gray-600")}
          >
            {event.is_active ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>
          <button title="Dry Run — preview reach" onClick={() => onDryRun && onDryRun(event)} className="p-1.5 rounded hover:bg-muted transition-colors text-yellow-500 hover:text-yellow-700">
            <Eye className="w-4 h-4" />
          </button>
          <button onClick={() => onEdit(event)} className="p-1.5 rounded hover:bg-muted transition-colors text-blue-500 hover:text-blue-700">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(event)} className="p-1.5 rounded hover:bg-muted transition-colors text-red-400 hover:text-red-600">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}


// ── AI Chat Event Categories ─────────────────────────────────────────────────
const AI_EVENT_CATEGORIES = [
  {
    value: "DIET_REMINDER",
    label: "Diet Intake Reminder",
    description: "Uses each customer’s Reminder Meal Times and timezone (not a fixed schedule here). Requires active AI membership; interactive AI follow-up in chat.",
    icon: UtensilsCrossed,
    color: "text-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-200",
    defaultEventType: "DIET_INTAKE_REMINDER",
  },
  {
    value: "WATER_REMINDER",
    label: "Water Reminder",
    description: "Remind customers to drink enough water throughout the day",
    icon: Droplets,
    color: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    defaultEventType: "INTERVAL",
  },
  {
    value: "HEALTH_RELATED",
    label: "Health Related Events",
    description: "When a customer logs food, AI warns if the item may conflict with their profile health issues (requires active AI membership)",
    icon: Heart,
    color: "text-rose-600",
    bg: "bg-rose-50",
    border: "border-rose-200",
    defaultEventType: "HEALTH_INTAKE",
  },
  {
    value: "CALORIE_OVERCONSUMPTION",
    label: "Yesterday Overcalories",
    description: "Alert customers who exceeded their daily calorie target yesterday",
    icon: TrendingUp,
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-200",
    defaultEventType: "DAILY",
  },
  {
    value: "INACTIVITY_3_DAYS",
    label: "3-Day Inactivity",
    description: "Re-engage customers who have not logged meals in the last 3 days",
    icon: CalendarX,
    color: "text-purple-600",
    bg: "bg-purple-50",
    border: "border-purple-200",
    defaultEventType: "INACTIVITY_3_DAYS",
  },
  {
    value: "LAST_SEEN_EVENT",
    label: "Event Based on Last Seen",
    description: "Trigger messages based on when customers last opened the app",
    icon: Clock,
    color: "text-slate-600",
    bg: "bg-muted",
    border: "border-slate-200",
    defaultEventType: "INACTIVITY",
  },
  {
    value: "AI_MEMBERSHIP_EXPIRED",
    label: "AI Membership Expired",
    description: "Every day at 12:00 AM (customer time) for customers whose AI plan is expired. Use {{name}} in the message.",
    icon: CreditCard,
    color: "text-yellow-600",
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    defaultEventType: "AI_MEMBERSHIP_EXPIRED",
  },
];

// ── AIEventModal ─────────────────────────────────────────────────────────────
function AIEventModal({ event, onClose, onSave }) {
  const isEdit = !!event?.id;
  const [step, setStep] = React.useState(isEdit ? 2 : 1);
  const [selectedCategory, setSelectedCategory] = React.useState(
    isEdit ? (AI_EVENT_CATEGORIES.find(c => c.value === event.ai_event_category) || AI_EVENT_CATEGORIES[0]) : null
  );
  const [form, setForm] = React.useState({
    name: event?.name || "",
    chat_type: "ai",
    event_type: event?.event_type || "DAILY",
    ai_event_category: event?.ai_event_category || "",
    schedule: event?.schedule || { time_of_day: "08:00" },
    message: event?.message || { elements: [] },
    is_active: event?.is_active !== false,
    max_sends_per_customer: event?.max_sends_per_customer ?? 0,
  });
  const [saving, setSaving] = React.useState(false);

  const upd = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleCategorySelect = (cat) => {
    setSelectedCategory(cat);
    const defType = cat.defaultEventType;
    setForm(f => {
      const next = {
        ...f,
        ai_event_category: cat.value,
        event_type: defType,
        schedule: defaultSchedule(defType),
        name: f.name || cat.label,
      };
      if (cat.value === "INACTIVITY_3_DAYS") {
        next.message = {
          elements: [
            {
              type: "text",
              content: "Hello {{name}}, you have not used the app for the last 3 days.",
              id: Date.now(),
            },
          ],
        };
      }
      if (cat.value === "AI_MEMBERSHIP_EXPIRED") {
        next.message = {
          elements: [
            {
              type: "text",
              content: "Hello {{name}}, your AI membership is expired.",
              id: Date.now(),
            },
          ],
        };
      }
      if (cat.value === "DIET_REMINDER") {
        next.message = {
          elements: [
            {
              type: "text",
              content:
                "Hello {{name}}, just a follow-up on what you had for this meal. Open AI Chat from the menu — you’ll see this message there. Tell me what you ate and I’ll ask portions (grams, roti count, drinks in ml) and log it.",
              id: Date.now(),
            },
          ],
        };
      }
      return next;
    });
    setStep(2);
  };

  const handleTypeChange = (val) => {
    setForm(f => ({ ...f, event_type: val, schedule: defaultSchedule(val) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Event name is required"); return; }
    const textElems = form.message.elements.filter(el => el.type === "text" && el.content?.trim());
    if (form.event_type !== "HEALTH_INTAKE" && textElems.length === 0) {
      toast.error("At least one text block with content is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        message: { elements: form.message.elements.map(el => { const { id, ...rest } = el; return rest; }) }
      };
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  // Step 1: Category Selection
  if (step === 1) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Bot className="w-5 h-5 text-violet-500" />
              Add AI Chat Event
            </DialogTitle>
            <DialogDescription>
              Choose a category to define what triggers this AI message.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-1 pb-2">
            {AI_EVENT_CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.value}
                  onClick={() => handleCategorySelect(cat)}
                  data-testid={"ai-event-cat-" + cat.value}
                  className={cn(
                    "flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all hover:scale-[1.01] hover:shadow-md active:scale-[0.99]",
                    cat.border,
                    cat.bg
                  )}
                >
                  <div className={cn(
                    "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-card shadow-sm border",
                    cat.border
                  )}>
                    <Icon className={cn("w-5 h-5", cat.color)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-sm font-semibold leading-tight", cat.color)}>{cat.label}</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-snug">{cat.description}</p>
                  </div>
                  <ChevronRight className={cn("w-4 h-4 shrink-0 mt-1 opacity-60", cat.color)} />
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Step 2: Configuration
  const catInfo = selectedCategory || AI_EVENT_CATEGORIES[0];
  const CatIcon = catInfo?.icon;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[75vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            {!isEdit && (
              <button
                type="button"
                onClick={() => setStep(1)}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div className="min-w-0">
              <DialogTitle className="flex items-center gap-2">
                {CatIcon && <CatIcon className={cn("w-5 h-5 shrink-0", catInfo.color)} />}
                <span>{isEdit ? "Edit: " + event.name : catInfo?.label}</span>
              </DialogTitle>
              <DialogDescription className="mt-0.5">{catInfo?.description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {!isEdit && (
            <div className={cn("inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border", catInfo.border, catInfo.bg, catInfo.color)}>
              {CatIcon && <CatIcon className="w-3 h-3" />}
              {catInfo.label}
            </div>
          )}

          <div className="space-y-2">
            <Label>Event Name *</Label>
            <Input
              value={form.name}
              onChange={e => upd("name", e.target.value)}
              placeholder="e.g. Daily Diet Reminder, Morning Water Check"
              data-testid="ai-event-name-input"
            />
          </div>

          {(catInfo?.isCustom || isEdit) && (
            <div className="space-y-2">
              <Label>Trigger Type *</Label>
              <Select value={form.event_type} onValueChange={handleTypeChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <div>
                        <div className="font-medium">{t.label}</div>
                        <div className="text-xs text-muted-foreground">{t.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="border border-border rounded-lg p-4 bg-muted/20">
            <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
              {form.event_type === "DIET_INTAKE_REMINDER" ? "Timing (customer profile)" : "Schedule Configuration"}
            </p>
            <ScheduleConfig
              eventType={form.event_type}
              schedule={form.schedule}
              onChange={s => upd("schedule", s)}
            />
          </div>

          {form.event_type === "HEALTH_INTAKE" ? (
            <div className="rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
              The automated chat message is generated by AI from the logged food and the customer&apos;s health issues. You do not need message blocks for delivery (optional blocks are ignored for this trigger).
            </div>
          ) : (
            <>
              <MessageBuilder
                elements={form.message.elements}
                onChange={els => upd("message", { elements: els })}
              />
              {(form.event_type === "INACTIVITY_3_DAYS" || form.event_type === "AI_MEMBERSHIP_EXPIRED") && (
                <p className="text-xs text-muted-foreground -mt-1">
                  Placeholder <code className="text-foreground">{"{{name}}"}</code> is replaced with the customer&apos;s name when the message is sent.
                </p>
              )}
            </>
          )}

          <div className="space-y-2">
            <Label>Max sends per customer (0 = unlimited)</Label>
            <Input
              type="number"
              min="0"
              value={form.max_sends_per_customer}
              onChange={e => upd("max_sends_per_customer", Number(e.target.value))}
              placeholder="0"
            />
            <p className="text-xs text-muted-foreground">Stop sending after N deliveries per customer. 0 = unlimited.</p>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
            <Switch checked={form.is_active} onCheckedChange={v => upd("is_active", v)} />
            <div>
              <Label className="cursor-pointer">{form.is_active ? "Event is Active" : "Event is Inactive"}</Label>
              <p className="text-xs text-muted-foreground">
                {form.is_active
                  ? (form.event_type === "HEALTH_INTAKE"
                    ? "Event runs when customers log food intake"
                    : form.event_type === "INACTIVITY_3_DAYS"
                    ? "Daily at scheduled time for customers inactive 3+ days (diet log)"
                    : form.event_type === "AI_MEMBERSHIP_EXPIRED"
                    ? "Daily at scheduled time for customers with expired AI membership"
                    : form.event_type === "DIET_INTAKE_REMINDER"
                    ? "Runs when each customer’s reminder meal time matches local clock (AI membership required)"
                    : form.event_type === "AI_FIRST_USE_APP_RATING"
                    ? "Legacy event type — configure in-app rating under Settings → URLs instead"
                    : "Event will fire on schedule")
                  : "Event is paused and will not fire"}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="ai-event-submit-btn">
              {saving ? "Saving..." : isEdit ? "Update Event" : "Create Event"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EventModal({ event, chatType, onClose, onSave }) {
  const isEdit = !!event?.id;
  const [form, setForm] = React.useState({
    name: event?.name || "",
    chat_type: event?.chat_type || chatType,
    event_type: event?.event_type || "AFTER_SIGNUP",
    schedule: event?.schedule || { delay_minutes: 60 },
    message: event?.message || { elements: [] },
    is_active: event?.is_active !== false,
    max_sends_per_customer: event?.max_sends_per_customer ?? 0,
  });
  const [saving, setSaving] = React.useState(false);

  const upd = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleTypeChange = (val) => {
    setForm(f => ({ ...f, event_type: val, schedule: defaultSchedule(val) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Event name is required"); return; }
    const textElems = form.message.elements.filter(el => el.type === "text" && el.content?.trim());
    if (textElems.length === 0) { toast.error("At least one text block with content is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, message: { elements: form.message.elements.map(el => { const { id, ...rest } = el; return rest; }) } };
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  const typeInfo = EVENT_TYPES.find(t => t.value === form.event_type);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[75vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Event" : "Add Event"}</DialogTitle>
          <DialogDescription>
            Configure an automated message event for {chatType === "direct" ? "Direct Chat" : "AI Chat"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label>Event Name *</Label>
            <Input value={form.name} onChange={e => upd("name", e.target.value)} placeholder="e.g. Welcome Message, Daily Reminder" />
          </div>

          <div className="space-y-2">
            <Label>Event Type *</Label>
            <Select value={form.event_type} onValueChange={handleTypeChange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>
                    <div>
                      <div className="font-medium">{t.label}</div>
                      <div className="text-xs text-muted-foreground">{t.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border border-border rounded-lg p-4 bg-muted/20">
            <p className="text-xs font-medium text-muted-foreground mb-3">SCHEDULE CONFIGURATION</p>
            <ScheduleConfig eventType={form.event_type} schedule={form.schedule} onChange={s => upd("schedule", s)} />
          </div>

          <MessageBuilder elements={form.message.elements} onChange={els => upd("message", { elements: els })} />

                    <div className="space-y-2">
            <Label>Max sends per customer (0 = unlimited)</Label>
            <Input type="number" min="0" value={form.max_sends_per_customer} onChange={e => upd("max_sends_per_customer", Number(e.target.value))} placeholder="0" />
            <p className="text-xs text-muted-foreground">Stop sending after N deliveries per customer. 0 = unlimited.</p>
          </div>
<div className="flex items-center gap-3">
            <Switch checked={form.is_active} onCheckedChange={v => upd("is_active", v)} />
            <Label>{form.is_active ? "Event is Active" : "Event is Inactive"}</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Saving..." : isEdit ? "Update Event" : "Create Event"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EventsList({ chatType, onDryRun }) {
  // companyAdminCustomersAPI is imported at the top of this file
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showModal, setShowModal] = React.useState(false);
  const [editingEvent, setEditingEvent] = React.useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await companyAdminCustomersAPI.listEvents({ chat_type: chatType });
      setEvents(res.data?.events || []);
    } catch {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, [chatType]);

  const handleSave = async (payload) => {
    try {
      if (editingEvent?.id) {
        await companyAdminCustomersAPI.updateEvent(editingEvent.id, payload);
        toast.success("Event updated");
      } else {
        await companyAdminCustomersAPI.createEvent(payload);
        toast.success("Event created");
      }
      setShowModal(false);
      setEditingEvent(null);
      load();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Failed to save event";
      toast.error(msg);
      // Do not rethrow — avoids React error overlay on expected API errors (e.g. 403 hybrid chat).
    }
  };

  const handleToggle = async (event) => {
    try {
      await companyAdminCustomersAPI.updateEvent(event.id, { is_active: !event.is_active });
      toast.success(event.is_active ? "Event disabled" : "Event enabled");
      load();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Failed to update event";
      toast.error(msg);
    }
  };

  const handleDelete = async (event) => {
    if (!window.confirm(`Delete event "${event.name}"?`)) return;
    try {
      await companyAdminCustomersAPI.deleteEvent(event.id);
      toast.success("Event deleted");
      load();
    } catch {
      toast.error("Failed to delete event");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">{chatType === "direct" ? "Direct Chat" : "AI Chat"} Events</h3>
          <p className="text-sm text-muted-foreground">Automated messages sent to customers based on triggers</p>
        </div>
        <Button onClick={() => { setEditingEvent(null); setShowModal(true); }} size="sm">
          + Add Event
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading events...</div>
      ) : events.length === 0 ? (
        <div className="border-2 border-dashed border-muted rounded-xl p-10 text-center">
          <Zap className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <h4 className="font-medium mb-1">No events yet</h4>
          <p className="text-sm text-muted-foreground mb-4">Create your first automated message event</p>
          <Button onClick={() => { setEditingEvent(null); setShowModal(true); }}>Create First Event</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(ev => (
            <EventCard
              key={ev.id}
              event={ev}
              onEdit={e => { setEditingEvent(e); setShowModal(true); }}
              onDelete={handleDelete}
              onToggle={handleToggle}
              onDryRun={onDryRun}
            />
          ))}
        </div>
      )}

      {showModal && (
        chatType === "ai" ? (
          <AIEventModal
            event={editingEvent}
            onClose={() => { setShowModal(false); setEditingEvent(null); }}
            onSave={handleSave}
          />
        ) : (
          <EventModal
            event={editingEvent}
            chatType={chatType}
            onClose={() => { setShowModal(false); setEditingEvent(null); }}
            onSave={handleSave}
          />
        )
      )}
    </div>
  );
}

// ── Queue Health Badge ─────────────────────────────────────────────────────
function QueueHealthBadge({ queueStatus }) {
  if (!queueStatus) return null;
  const { waiting = 0, active = 0, failed = 0, delayed = 0 } = queueStatus.counts || {};
  const hasIssues = failed > 0;
  const isIdle = waiting === 0 && active === 0;
  return (
    <div className={cn("flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border", hasIssues ? "bg-red-50 border-red-200 text-red-700" : isIdle ? "bg-green-50 border-green-200 text-green-700" : "bg-blue-50 border-blue-200 text-blue-700")}>
      <div className={cn("w-2 h-2 rounded-full", hasIssues ? "bg-red-500" : isIdle ? "bg-green-500 animate-pulse" : "bg-blue-500")} />
      <span>Queue: {active > 0 ? `${active} active` : "idle"}{waiting > 0 ? ` · ${waiting} waiting` : ""}{delayed > 0 ? ` · ${delayed} delayed` : ""}{failed > 0 ? ` · ${failed} failed` : ""}</span>
    </div>
  );
}

// ── Summary Stats Cards ────────────────────────────────────────────────────
function StatsCards({ stats }) {
  if (!stats) return null;
  const { totals = {}, success_rate = 0 } = stats;
  const cards = [
    { label: "Total Sent", value: totals.sent || 0, color: "text-green-600", bg: "bg-green-50 border-green-100" },
    { label: "Failed", value: totals.failed || 0, color: "text-red-600", bg: "bg-red-50 border-red-100" },
    { label: "Skipped", value: totals.skipped || 0, color: "text-yellow-600", bg: "bg-yellow-50 border-yellow-100" },
    { label: "Success Rate", value: success_rate + "%", color: "text-blue-600", bg: "bg-blue-50 border-blue-100" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {cards.map(c => (
        <div key={c.label} className={cn("rounded-lg border p-3", c.bg)}>
          <div className={cn("text-2xl font-bold", c.color)}>{c.value}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{c.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Daily Delivery Chart ───────────────────────────────────────────────────
function DeliveryChart({ dailySeries }) {
  if (!dailySeries || !dailySeries.length) {
    return <div className="text-sm text-muted-foreground text-center py-6">No delivery data yet</div>;
  }

  // Transform series data: [{_id: {date, status}, count}] → [{date, sent, failed, skipped}]
  const byDate = {};
  for (const row of dailySeries) {
    const date = row._id.date;
    if (!byDate[date]) byDate[date] = { date, sent: 0, failed: 0, skipped: 0 };
    byDate[date][row._id.status] = (byDate[date][row._id.status] || 0) + row.count;
  }
  const data = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="h-48">
      <p className="text-xs font-medium text-muted-foreground mb-2">DELIVERIES — LAST 7 DAYS</p>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
          <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
          <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
          <Tooltip formatter={(val, name) => [val, name.charAt(0).toUpperCase() + name.slice(1)]} labelFormatter={l => `Date: ${l}`} />
          <Bar dataKey="sent" stackId="a" fill="#22c55e" radius={[0,0,0,0]} />
          <Bar dataKey="failed" stackId="a" fill="#ef4444" radius={[0,0,0,0]} />
          <Bar dataKey="skipped" stackId="a" fill="#f59e0b" radius={[2,2,0,0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Top Events List ────────────────────────────────────────────────────────
function TopEventsList({ topEvents }) {
  if (!topEvents || !topEvents.length) return null;
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-2">TOP PERFORMING EVENTS</p>
      <div className="space-y-1.5">
        {topEvents.map((ev, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <span className="truncate flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-bold">{i+1}</span>
              {ev.name || "Unnamed"}
            </span>
            <span className="text-green-600 font-medium ml-2">{ev.sent} sent</span>
          </div>
        ))}
      </div>
    </div>
  );
}


const EventsTab = () => {
  const [activeSubTab, setActiveSubTab] = React.useState("direct");
  const [activeView, setActiveView] = React.useState("events"); // "events" | "analytics"
  const [stats, setStats] = React.useState(null);
  const [queueStatus, setQueueStatus] = React.useState(null);
  const [dryRunResult, setDryRunResult] = React.useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = React.useState(false);

  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const [statsRes, queueRes] = await Promise.all([
        companyAdminCustomersAPI.getEventsStats(),
        companyAdminCustomersAPI.getQueueStatus(),
      ]);
      setStats(statsRes.data);
      setQueueStatus(queueRes.data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoadingAnalytics(false);
    }
  };

  React.useEffect(() => {
    loadAnalytics();
    // Refresh queue status every 15s
    const interval = setInterval(() => {
      companyAdminCustomersAPI.getQueueStatus().then(r => setQueueStatus(r.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleDryRun = async (event) => {
    try {
      const res = await companyAdminCustomersAPI.dryRunEvent(event.id);
      setDryRunResult({ event, ...res.data });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Dry run failed";
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold">Automated Events</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Rule-based messages delivered via the existing chat pipeline
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <QueueHealthBadge queueStatus={queueStatus} />
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            <button onClick={() => setActiveView("events")} className={cn("px-3 py-1.5 rounded-md text-sm font-medium transition-all", activeView === "events" ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground")}>
              Events
            </button>
            <button onClick={() => { setActiveView("analytics"); loadAnalytics(); }} className={cn("px-3 py-1.5 rounded-md text-sm font-medium transition-all", activeView === "analytics" ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground")}>
              Analytics
            </button>
          </div>
        </div>
      </div>

      {/* Dry Run Result Modal */}
      {dryRunResult && (
        <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-yellow-800">Dry Run: {dryRunResult.event?.name}</h4>
            <button onClick={() => setDryRunResult(null)} className="text-yellow-600 hover:text-yellow-800"><XCircle className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="text-2xl font-bold text-yellow-700">{dryRunResult.total_customers}</div><div className="text-xs text-yellow-600">Total Customers</div></div>
            <div><div className="text-2xl font-bold text-green-700">{dryRunResult.will_receive}</div><div className="text-xs text-green-600">Will Receive</div></div>
            <div><div className="text-2xl font-bold text-muted-foreground">{dryRunResult.already_sent_count}</div><div className="text-xs text-muted-foreground">Already Sent</div></div>
          </div>
          {dryRunResult.message_preview && (
            <div className="mt-3 p-2 bg-card rounded border border-yellow-200 text-xs text-foreground italic">"{dryRunResult.message_preview}"</div>
          )}
        </div>
      )}

      {activeView === "events" ? (
        <>
          {/* Chat type sub-tabs */}
          <div className="flex gap-1 bg-muted rounded-lg p-1 w-fit">
            <button onClick={() => setActiveSubTab("direct")} className={cn("px-4 py-1.5 rounded-md text-sm font-medium transition-all", activeSubTab === "direct" ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground")}>
              Direct Chat Events
            </button>
            <button onClick={() => setActiveSubTab("ai")} className={cn("px-4 py-1.5 rounded-md text-sm font-medium transition-all", activeSubTab === "ai" ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground")}>
              AI Chat Events
            </button>
          </div>
          <EventsList key={activeSubTab} chatType={activeSubTab} onDryRun={handleDryRun} />
        </>
      ) : (
        <div className="space-y-6">
          {loadingAnalytics ? (
            <div className="text-center py-8 text-muted-foreground">Loading analytics...</div>
          ) : (
            <>
              <StatsCards stats={stats} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="border border-border rounded-lg p-4">
                  <DeliveryChart dailySeries={stats?.daily_series} />
                </div>
                <div className="border border-border rounded-lg p-4 space-y-4">
                  <TopEventsList topEvents={stats?.top_events} />
                  {queueStatus && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">QUEUE DETAILS</p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        {Object.entries(queueStatus.counts || {}).map(([k, v]) => (
                          <div key={k} className="bg-muted rounded p-2 text-center">
                            <div className="font-bold text-sm">{v}</div>
                            <div className="text-muted-foreground capitalize">{k}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <Button variant="outline" size="sm" onClick={loadAnalytics} className="w-full">
                    <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const SettingsPage = () => {
  return (
    <CompanyAdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your account and system settings</p>
        </div>

        <Tabs defaultValue="personal" className="w-full">
          <TabsList className="flex flex-wrap gap-1 h-auto border-b pb-0 bg-transparent justify-start">
            <TabsTrigger value="personal" className="flex items-center gap-2 py-2">
              <User className="w-4 h-4" />
              <span className="hidden md:inline">Personal Info</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2 py-2">
              <Shield className="w-4 h-4" />
              <span className="hidden md:inline">Security</span>
            </TabsTrigger>
            <TabsTrigger value="branding" className="flex items-center gap-2 py-2">
              <Palette className="w-4 h-4" />
              <span className="hidden md:inline">Branding</span>
            </TabsTrigger>
            <TabsTrigger value="appearance" className="flex items-center gap-2 py-2" data-testid="tab-appearance">
              <Sun className="w-4 h-4" />
              <span className="hidden md:inline">Appearance</span>
            </TabsTrigger>
            <TabsTrigger value="captcha" className="flex items-center gap-2 py-2">
              <Bot className="w-4 h-4" />
              <span className="hidden md:inline">CAPTCHA</span>
            </TabsTrigger>
            <TabsTrigger value="ftp" className="flex items-center gap-2 py-2">
              <Server className="w-4 h-4" />
              <span className="hidden md:inline">FTP Backup</span>
            </TabsTrigger>
            <TabsTrigger value="urls" className="flex items-center gap-2 py-2" data-testid="tab-urls">
              <Globe className="w-4 h-4" />
              <span className="hidden md:inline">URLs</span>
            </TabsTrigger>
            <TabsTrigger value="login-auth" className="flex items-center gap-2 py-2" data-testid="tab-login-auth">
              <KeyRound className="w-4 h-4" />
              <span className="hidden md:inline">Login Auth</span>
            </TabsTrigger>
            <TabsTrigger value="schedulers" className="flex items-center gap-2 py-2">
              <Clock className="w-4 h-4" />
              <span className="hidden md:inline">Schedulers</span>
            </TabsTrigger>
            <TabsTrigger value="mailer" className="flex items-center gap-2 py-2" data-testid="tab-mailer">
              <Mail className="w-4 h-4" />
              <span className="hidden md:inline">Mailer</span>
            </TabsTrigger>
            <TabsTrigger value="roles" className="flex items-center gap-2 py-2" data-testid="tab-roles">
              <Briefcase className="w-4 h-4" />
              <span className="hidden md:inline">Roles</span>
            </TabsTrigger>
            <TabsTrigger value="events" className="flex items-center gap-2 py-2" data-testid="tab-events">
              <Zap className="w-4 h-4" />
              <span className="hidden md:inline">Events</span>
            </TabsTrigger>
          </TabsList>

          <div className="mt-6 bg-card rounded-lg border border-border p-6">
            <TabsContent value="personal" className="mt-0">
              <PersonalInfoTab />
            </TabsContent>
            <TabsContent value="security" className="mt-0">
              <SecurityTab />
            </TabsContent>
            <TabsContent value="branding" className="mt-0">
              <BrandingTab />
            </TabsContent>
            <TabsContent value="appearance" className="mt-0">
              <AppearanceTab />
            </TabsContent>
            <TabsContent value="captcha" className="mt-0">
              <CaptchaTab />
            </TabsContent>
            <TabsContent value="ftp" className="mt-0">
              <FtpTab />
            </TabsContent>
            <TabsContent value="urls" className="mt-0">
              <UrlsTab />
            </TabsContent>
            <TabsContent value="login-auth" className="mt-0">
              <LoginAuthTab />
            </TabsContent>
            <TabsContent value="schedulers" className="mt-0">
              <SchedulersTab />
            </TabsContent>
            <TabsContent value="mailer" className="mt-0">
              <MailerTab />
            </TabsContent>
            <TabsContent value="roles" className="mt-0">
              <RolesTab />
            </TabsContent>
            <TabsContent value="events" className="mt-0">
              <EventsTab />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </CompanyAdminLayout>
  );
};

export default SettingsPage;
